/**
 * HomeDashboardTiles.tsx
 *
 * Extracted from HomeScreen.tsx JSX.
 * Two rows of metric tiles: Energy (Activity Burn, Total Burn)
 * and Body/Behaviour (Protein, Weight, Hydration).
 * Target: src/screens/Home/HomeDashboardTiles.tsx
 */

import React from 'react';
import {
  getHydrationUnitLabel,
  getBodyWeightUnitLabel,
  formatLabelWithUnit,
} from '../../utils/unitLabels';

// ── Horseshoe arc constants ──
// ARC_PATH: 230° horseshoe, open at bottom, left tip → right tip clockwise.
// pathLength="88.3" on the fill path normalises browser arc-length calculation
// so strokeDasharray units map exactly to ARC_LEN — fill starts at left tip,
// gap sits at the right end.
const ARC_LEN = 88.3;
const MIN_GAP = 4;
const ARC_PATH = 'M9.08,38.94 A22,22 0 1,1 44.92,38.94';

function fillDash(value: number, max: number): string {
  if (!max || max <= 0 || value == null) return `0 ${ARC_LEN}`;
  if (value <= 0) return `0 ${ARC_LEN}`;
  if (value >= max) return `${ARC_LEN} 0`;
  const raw = (value / max) * ARC_LEN;
  const fill = Math.min(raw, ARC_LEN - MIN_GAP);
  return `${fill.toFixed(1)} ${(ARC_LEN - fill).toFixed(1)}`;
}

// RAG colour bands (progress = value / target, clamped 0–1+)
// Red: 0.00–0.33 | Yellow: 0.34–0.66 | Green: 0.67+
const RAG_RED    = '#FF0000';
const RAG_YELLOW = '#EAB308';
const RAG_GREEN  = '#2FBF71';
const TICK_COLOUR = RAG_GREEN;

function ragColour(progress: number): string {
  if (progress >= 0.67) return RAG_GREEN;
  if (progress >= 0.34) return RAG_YELLOW;
  return RAG_RED;
}

interface HorseshoeArcProps {
  value: number;
  target: number;  // the threshold used for progress calculation
  max: number;     // upper bound for arc fill length
  isComplete: boolean;
}

const HorseshoeArc: React.FC<HorseshoeArcProps> = ({ value, target, max, isComplete }) => {
  const progress = (target > 0 && value != null) ? value / target : 0;
  const dash = fillDash(value, max);
  const colour = ragColour(progress);
  return (
    <svg width="54" height="40" viewBox="0 0 54 44" style={{ display: 'block' }}>
      <path d={ARC_PATH} fill="none" stroke="#A8A29B" strokeWidth={7} strokeLinecap="round" />
      <path
        d={ARC_PATH}
        fill="none"
        stroke={colour}
        strokeWidth={5}
        strokeLinecap="round"
        pathLength={ARC_LEN}
        strokeDasharray={dash}
      />
      {isComplete && (
        <polyline
          points="20,22 25,28 35,17"
          fill="none"
          stroke={TICK_COLOUR}
          strokeWidth={2.8}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
};

interface HomeDashboardTilesProps {
  formattedWeight: string;
  formattedProtein: string;
  formattedHydration: string;
  hydrationRaw: number;
  proteinRaw: number;
  foodUnits: any;
  bodyUnits: any;
  targets: any;
  onNavigate: (path: string) => void;
}

export const HomeDashboardTiles: React.FC<HomeDashboardTilesProps> = ({
  formattedWeight,
  formattedProtein,
  formattedHydration,
  hydrationRaw,
  proteinRaw,
  foodUnits,
  bodyUnits,
  targets,
  onNavigate,
}) => {
  const tileStyle = {
    background: 'rgba(255,255,255,0.93)',
    border: '1px solid rgba(225,225,225,0.4)',
    boxShadow: '0px 4px 22px 0px rgba(0,0,0,0.012)',
    padding: '9px 8px 7px',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    alignItems: 'center' as const,
    gap: 6,
  };

  const proteinMin = targets?.protein?.min ?? 0;
  const proteinMax = targets?.protein?.max ?? 0;
  const hydrationMin = targets?.hydration?.min ?? 0;

  const proteinComplete = proteinMax > 0 && proteinRaw >= proteinMax;
  const hydrationComplete = hydrationMin > 0 && hydrationRaw >= hydrationMin;

  return (
    <>
      {/* Protein | Weight | Hydration */}
      <div className="grid grid-cols-3 gap-2">
        {/* Protein */}
        <div className="rounded-[20px]" style={tileStyle}>
          <HorseshoeArc value={proteinRaw} target={proteinMin} max={proteinMax} isComplete={proteinComplete} />
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 11, fontWeight: 400, color: '#6B7280', letterSpacing: -0.2, marginBottom: 4, marginTop: 0 }}>
              Protein (g)
            </p>
            <p style={{ fontSize: 16, fontWeight: 600, color: '#0d0d0d', letterSpacing: -0.2, margin: 0, fontVariantNumeric: 'tabular-nums' }}>
              {formattedProtein}
            </p>
          </div>
        </div>

        {/* Weight — centre */}
        <button
          type="button"
          onClick={() => onNavigate('/body-metrics')}
          className="rounded-[20px] text-left"
          style={{ ...tileStyle, cursor: 'pointer' }}
        >
          <svg width="40" height="40" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', opacity: 0.75 }}>
            <path fill="#1a1a1a" d="M398.592,14H115.409A100.522,100.522,0,0,0,15,114.409V397.592A100.522,100.522,0,0,0,115.409,498H398.592A100.522,100.522,0,0,0,499,397.592V114.409A100.522,100.522,0,0,0,398.592,14ZM487,397.592A88.509,88.509,0,0,1,398.592,486H115.409A88.509,88.509,0,0,1,27,397.592V114.409A88.509,88.509,0,0,1,115.409,26H398.592A88.509,88.509,0,0,1,487,114.409Z"/>
            <path fill="#1a1a1a" d="M402.23,83H111.77A27.8,27.8,0,0,0,84,110.77V401.23A27.8,27.8,0,0,0,111.77,429H402.23A27.8,27.8,0,0,0,430,401.23V110.77A27.8,27.8,0,0,0,402.23,83ZM335,95v44.58A15.437,15.437,0,0,1,319.58,155H194.42A15.437,15.437,0,0,1,179,139.58V95Zm83,306.23A15.787,15.787,0,0,1,402.23,417H111.77A15.787,15.787,0,0,1,96,401.23V110.77A15.787,15.787,0,0,1,111.77,95H167v44.58A27.45,27.45,0,0,0,194.42,167H319.58A27.451,27.451,0,0,0,347,139.58V95h55.23A15.787,15.787,0,0,1,418,110.77Z"/>
            <path fill="#1a1a1a" d="M224.1,321.2c3.573-15.7,5.273-37.458-.706-52.686a53,53,0,1,0-98.668,38.737c4.819,12.274,17.868,26.887,33.794,38.041a41.1,41.1,0,0,0,2.026,23.1c3.966,10.1,12.424,19.958,22.624,26.367,7.638,4.8,15.422,7.246,22.734,7.246a30.159,30.159,0,0,0,11.079-2.055c21.14-8.3,28.167-40.445,19.892-61.526A40.688,40.688,0,0,0,224.1,321.2Zm-11.5,67.577c-6.409,2.517-14.809.993-23.043-4.181-7.987-5.019-14.822-12.909-17.839-20.592a29.023,29.023,0,0,1-.616-19.47,6,6,0,0,0-2.441-6.874c-15.34-9.968-28.506-23.951-32.763-34.794A41,41,0,1,1,212.221,272.9c4.42,11.26,4.128,31.072-.726,49.3a6,6,0,0,0,2.614,6.63A28.787,28.787,0,0,1,225.7,342.807C231.823,358.4,226.879,383.169,212.6,388.777Z"/>
            <path fill="#1a1a1a" d="M188.923,231.7a13.71,13.71,0,0,0,2.581-.245c9.913-1.9,15.615-14.17,12.979-27.938s-12.467-23.068-22.379-21.171-15.614,14.169-12.979,27.938C171.531,222.853,179.934,231.7,188.923,231.7ZM184.36,194.13a1.8,1.8,0,0,1,.341-.031c2.42,0,6.595,4.353,8,11.672,1.47,7.675-1.1,13.446-3.449,13.9s-6.868-3.965-8.337-11.64S182.015,194.579,184.36,194.13Z"/>
            <path fill="#1a1a1a" d="M151.967,225.762a6,6,0,1,0,11.286-4.077L158.31,208a6,6,0,0,0-11.287,4.077Z"/>
            <path fill="#1a1a1a" d="M135.783,235.963a6,6,0,1,0,10.434-5.926l-9.991-17.592a6,6,0,0,0-10.435,5.927Z"/>
            <path fill="#1a1a1a" d="M123.2,249.488a6,6,0,1,0,9.13-7.788l-11.16-13.082a6,6,0,0,0-9.13,7.788Z"/>
            <path fill="#1a1a1a" d="M121.676,258.745l-9-10.545a6,6,0,0,0-9.13,7.788l9,10.544a6,6,0,0,0,9.13-7.787Z"/>
            <path fill="#1a1a1a" d="M359.313,238.548a53.063,53.063,0,0,0-68.7,29.966c-5.979,15.228-4.28,36.986-.706,52.686a40.675,40.675,0,0,0-12.774,17.222c-8.276,21.08-1.249,53.225,19.892,61.525A30.13,30.13,0,0,0,308.1,402c7.311,0,15.1-2.447,22.733-7.246,10.2-6.409,18.659-16.267,22.624-26.366a41.085,41.085,0,0,0,2.027-23.1c15.925-11.153,28.974-25.765,33.794-38.041A53.059,53.059,0,0,0,359.313,238.548Zm18.794,64.318c-4.257,10.844-17.424,24.827-32.764,34.794a6,6,0,0,0-2.441,6.875A29.008,29.008,0,0,1,342.286,364c-3.016,7.683-9.851,15.573-17.838,20.592-8.234,5.174-16.631,6.7-23.043,4.181-14.284-5.608-19.228-30.379-13.106-45.969a28.788,28.788,0,0,1,11.592-13.98,6,6,0,0,0,2.615-6.63c-4.856-18.228-5.148-38.04-.727-49.3a41,41,0,1,1,76.328,29.968Z"/>
            <path fill="#1a1a1a" d="M322.5,231.453a13.717,13.717,0,0,0,2.581.245c8.987,0,17.392-8.846,19.8-21.416,2.636-13.768-3.065-26.04-12.978-27.938s-19.745,7.4-22.379,21.171h0C306.882,217.284,312.583,229.555,322.5,231.453ZM321.3,205.771c1.469-7.674,5.974-12.1,8.338-11.641,2.345.449,4.918,6.221,3.449,13.9s-5.992,12.093-8.337,11.641-4.918-6.221-3.449-13.9Z"/>
            <path fill="#1a1a1a" d="M354.352,229.367a6,6,0,0,0,7.681-3.6l4.944-13.686A6,6,0,0,0,355.689,208l-4.943,13.686A6,6,0,0,0,354.352,229.367Z"/>
            <path fill="#1a1a1a" d="M370.036,238.217a6,6,0,0,0,8.181-2.254l9.992-17.591a6,6,0,1,0-10.434-5.927l-9.992,17.592A6,6,0,0,0,370.036,238.217Z"/>
            <path fill="#1a1a1a" d="M382.34,250.159a6,6,0,0,0,8.458-.671l11.159-13.082a6,6,0,1,0-9.129-7.788L381.669,241.7A6,6,0,0,0,382.34,250.159Z"/>
            <path fill="#1a1a1a" d="M409.777,247.529a6,6,0,0,0-8.458.671l-9,10.545a6,6,0,1,0,9.129,7.787l9-10.544A6,6,0,0,0,409.777,247.529Z"/>
            <path fill="#1a1a1a" d="M206,131h10.5a6,6,0,0,0,0-12H206a6,6,0,0,0,0,12Z"/>
            <path fill="#1a1a1a" d="M243.5,131h27a6,6,0,0,0,0-12h-27a6,6,0,0,0,0,12Z"/>
            <path fill="#1a1a1a" d="M297.5,131H308a6,6,0,0,0,0-12H297.5a6,6,0,0,0,0,12Z"/>
          </svg>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 11, fontWeight: 400, color: '#6B7280', letterSpacing: -0.2, marginBottom: 4, marginTop: 0 }}>
              {formatLabelWithUnit("Weight", getBodyWeightUnitLabel(bodyUnits))}
            </p>
            <p style={{ fontSize: 16, fontWeight: 600, color: '#0d0d0d', letterSpacing: -0.2, margin: 0, fontVariantNumeric: 'tabular-nums' }}>
              {formattedWeight}
            </p>
          </div>
        </button>

        {/* Hydration */}
        <div className="rounded-[20px]" style={tileStyle}>
          <HorseshoeArc value={hydrationRaw} target={hydrationMin} max={hydrationMin * 1.5} isComplete={hydrationComplete} />
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 11, fontWeight: 400, color: '#6B7280', letterSpacing: -0.2, marginBottom: 4, marginTop: 0 }}>
              {formatLabelWithUnit("Hydration", getHydrationUnitLabel(foodUnits))}
            </p>
            <p style={{
              fontSize: 16, fontWeight: 600, letterSpacing: -0.2, margin: 0, fontVariantNumeric: 'tabular-nums',
              color: '#141517',
            }}>
              {formattedHydration}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
